import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import './UserFeedbackPage.css';

interface Feedback {
  user_id: string;
  feed_back: string;
  created_at: string;
  user_name: string;
  phone_no: string;
  caller_id: string;
  caller_name: string;
  audio_url: string;
  updated_at: string;
}

const UserFeedbackPage: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchPhone, setSearchPhone] = useState('');
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [formData, setFormData] = useState({
    phone_number: '',
    feedback: '',
    audio_url: '',
  });
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [downloading, setDownloading] = useState(false);

  const handleSearch = async () => {
    if (!searchPhone.trim()) return;
    try {
      setSearching(true);
      setError(null);
      const data = await authService.getUserFeedbacksByPhone(searchPhone);
      setFeedbacks(data);
      setHasSearched(true);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      setError('Failed to load user feedbacks');
      setFeedbacks([]);
      setHasSearched(true);
    } finally {
      setSearching(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];

      recorder.ondataavailable = e => chunks.push(e.data);

      const timer = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      recorder.onstop = async () => {
        clearInterval(timer);
        const blob = new Blob(chunks, { type: 'audio/wav' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());

        // Automatically transcribe after recording stops
        await transcribeAudioBlob(blob);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  const transcribeAudioBlob = async (blob: Blob) => {
    try {
      setTranscribing(true);
      console.log('Starting transcription process...');

      // First upload the audio to get URL
      const uploadFormData = new FormData();
      uploadFormData.append('file', blob, 'temp-audio.wav');
      console.log('Uploading audio for transcription...');

      const uploadResponse = await authService.uploadSingleFile(uploadFormData);
      console.log('Upload response:', uploadResponse);

      if (uploadResponse.file_url) {
        console.log(
          'Calling transcription API with URL:',
          uploadResponse.file_url
        );
        // Then transcribe using the URL
        const transcriptionResponse = await authService.transcribeAudio(
          uploadResponse.file_url
        );
        console.log('Transcription response:', transcriptionResponse);

        setTranscription(transcriptionResponse.transcription);

        // Auto-fill feedback field with transcription if it's empty
        if (!formData.feedback) {
          setFormData(prev => ({
            ...prev,
            feedback: transcriptionResponse.transcription,
          }));
        }
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
      setError('Failed to transcribe audio');
    } finally {
      setTranscribing(false);
    }
  };

  const uploadAudio = async () => {
    if (!audioBlob) return '';

    try {
      setUploadingAudio(true);
      const uploadFormData = new FormData();
      uploadFormData.append('file', audioBlob, 'feedback-audio.wav');

      const response = await authService.uploadSingleFile(uploadFormData);
      return response.file_url || '';
    } catch (error) {
      console.error('Error uploading audio:', error);
      throw new Error('Failed to upload audio');
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);

      let audioUrl = formData.audio_url;
      if (audioBlob) {
        audioUrl = await uploadAudio();
      }

      await authService.submitUserFeedback({
        ...formData,
        audio_url: audioUrl,
      });

      setFormData({ phone_number: '', feedback: '', audio_url: '' });
      setAudioBlob(null);
      setRecordingTime(0);
      setTranscription('');
      setShowForm(false);

      if (searchPhone.trim()) {
        handleSearch();
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setError('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadAllReviews = async () => {
    try {
      setDownloading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/user-feedback/all`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token && { 'x-auth-token': token }),
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `all-reviews-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Failed to download reviews');
      }
    } catch (error) {
      console.error('Error downloading reviews:', error);
      setError('Failed to download reviews');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="user-feedback-page">
        <div className="content-header">
          <div className="header-content">
            <h1 className="page-title">User Feedback</h1>
            <p className="page-subtitle">Loading user feedback...</p>
          </div>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-feedback-page">
        <div className="content-header">
          <div className="header-content">
            <h1 className="page-title">User Feedback</h1>
            <p className="page-subtitle">View and manage user feedback</p>
          </div>
        </div>
        <div className="error-message">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="user-feedback-page">
      <div className="content-header">
        <div className="header-content">
          <h1 className="page-title">User Feedback</h1>
          <p className="page-subtitle">View and manage user feedback</p>
        </div>
        <button
          className="download-reviews-button"
          onClick={handleDownloadAllReviews}
          disabled={downloading}
        >
          {downloading ? 'Downloading...' : 'Download all reviews'}
        </button>
      </div>

      <div className="search-section">
        <div className="search-form">
          <input
            type="tel"
            value={searchPhone}
            onChange={e => setSearchPhone(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Enter phone number (without +91)"
            className="search-input"
          />
          <button
            onClick={handleSearch}
            disabled={searching || !searchPhone.trim()}
            className="search-button"
          >
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {hasSearched && (
        <div className="feedback-actions">
          <button
            className="add-feedback-button"
            onClick={() => {
              setFormData({ ...formData, phone_number: searchPhone });
              setShowForm(true);
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Feedback
          </button>
        </div>
      )}

      {feedbacks.length === 0 && hasSearched ? (
        <div className="empty-state">
          <svg
            width="60"
            height="60"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <h3>No feedback available</h3>
          <p>No user feedback has been submitted yet.</p>
        </div>
      ) : feedbacks.length > 0 ? (
        <div className="feedbacks-container">
          <div className="feedbacks-header">
            <p className="feedbacks-count">
              {feedbacks.length} feedback{feedbacks.length !== 1 ? 's' : ''}{' '}
              found
            </p>
          </div>
          <div className="feedbacks-grid">
            {feedbacks.map((feedback, index) => (
              <div
                key={`${feedback.user_id}-${index}`}
                className="feedback-card"
              >
                <div className="feedback-header">
                  <h4>{feedback.user_name || 'Anonymous User'}</h4>
                  {feedback.caller_name && (
                    <p className="caller-name">
                      Caller: {feedback.caller_name}
                    </p>
                  )}
                </div>
                <div className="feedback-content">
                  <p>{feedback.feed_back}</p>
                </div>
                <div className="feedback-footer">
                  <span className="feedback-date">
                    {new Date(feedback.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Submit User Feedback</h2>
              <button
                className="modal-close"
                onClick={() => setShowForm(false)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="feedback-form">
              <div className="form-group">
                <label htmlFor="phone_number">
                  Phone Number <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="phone_number"
                  value={formData.phone_number}
                  onChange={e =>
                    setFormData({ ...formData, phone_number: e.target.value })
                  }
                  placeholder="Enter phone number (without +91)"
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="feedback">Feedback</label>
                <textarea
                  id="feedback"
                  value={formData.feedback}
                  onChange={e =>
                    setFormData({ ...formData, feedback: e.target.value })
                  }
                  placeholder="Enter your feedback"
                  className="form-input"
                  rows={4}
                />
              </div>
              <div className="form-group">
                <label>Voice Recording</label>
                <div className="voice-recorder">
                  {!isRecording && !audioBlob && (
                    <button
                      type="button"
                      onClick={startRecording}
                      className="record-button"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                      </svg>
                      Start Recording
                    </button>
                  )}

                  {isRecording && (
                    <div className="recording-controls">
                      <div className="recording-indicator">
                        <div className="recording-dot"></div>
                        <span>
                          Recording... {Math.floor(recordingTime / 60)}:
                          {(recordingTime % 60).toString().padStart(2, '0')}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={stopRecording}
                        className="stop-button"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <rect x="6" y="6" width="12" height="12" />
                        </svg>
                        Stop
                      </button>
                    </div>
                  )}

                  {audioBlob && (
                    <div className="audio-preview">
                      <audio controls src={URL.createObjectURL(audioBlob)} />
                      <button
                        type="button"
                        onClick={() => {
                          setAudioBlob(null);
                          setRecordingTime(0);
                          setTranscription('');
                        }}
                        className="delete-audio"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {transcribing && (
                    <div className="transcribing-indicator">
                      <div className="loading-spinner-small"></div>
                      <span>Transcribing audio...</span>
                    </div>
                  )}

                  {transcription && (
                    <div className="transcription-result">
                      <label>Transcription:</label>
                      <div className="transcription-text">{transcription}</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting || uploadingAudio || transcribing}
                >
                  {transcribing
                    ? 'Transcribing Audio...'
                    : uploadingAudio
                      ? 'Uploading Audio...'
                      : submitting
                        ? 'Submitting...'
                        : 'Submit Feedback'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserFeedbackPage;
